import {
  CognitoUser,
  CognitoUserAttribute,
  CognitoRefreshToken,
  CognitoUserPool,
  CognitoUserSession,
  ICognitoUserAttributeData,
} from "amazon-cognito-identity-js";
import axios from "axios";
import { action, observable } from "mobx";
import { RouterAddress } from "../constants/RouterAddress";
import { USER_GROUP_ADMIN } from "../constants/constants";
import { ROLE_TYPE_ADMIN } from "../constants/RegisterConstants";
import { AuthMode } from "../features/auth/config/authExperience";
import { getSessionBrandOverride } from "../features/auth/config/brandOverride";

interface ISessionProperties {
  jwtToken: string;
  expirationTime: number;
  auth_Time: number;
  refreshToken: string;
  currentUserEmail: string;
  customRoleType: string;
  userId: string;
  userName: string;
  groups: string[];
  facilityType: string;
}

interface IUserContextFeatureFlags {
  allowMultiRole: boolean;
  allowRoleSwitching: boolean;
  allowFacilitySwitching: boolean;
}

interface IUserContextResponse {
  brandKey?: string | null;
  brandName?: string | null;
  authMode?: AuthMode | null;
  featureFlags?: Partial<IUserContextFeatureFlags> | null;
  user?: {
    platformAdmin?: boolean | null;
    legacyRoleType?: string | null;
    legacyFacilityType?: string | null;
  } | null;
  activeContext?: {
    roleType?: string | null;
    facilityType?: string | null;
    facilityId?: string | null;
    membershipId?: string | null;
  } | null;
  memberships?: any[];
  availableRoles?: string[];
  availableFacilities?: string[];
  permissions?: {
    canCreateFacility?: boolean | null;
    canManageWorkspace?: boolean | null;
    canManageWorkspaceRoles?: boolean | null;
    canInviteStaff?: boolean | null;
    canEditMemberRoles?: boolean | null;
    canRemoveMembers?: boolean | null;
  } | null;
  profile?: any;
}

export class AuthStore {
  private cognitoUser: CognitoUser | undefined;
  private pendingNewPasswordUser: CognitoUser | null = null;
  private refreshSessionPromise: Promise<string> | null = null;
  private userContextPromise: Promise<IUserContextResponse | null> | null = null;
  private AUTH_API_ENDPOINT: string = process.env.REACT_APP_AUTH_API_ENDPOINT!;

  private normalizeGroups(groups: string[] | string | null | undefined): string[] {
    if (Array.isArray(groups)) {
      return groups.map((group) => String(group || "").trim()).filter(Boolean);
    }

    return String(groups || "")
      .split(",")
      .map((group) => group.trim())
      .filter(Boolean);
  }

  @observable sessionProp: ISessionProperties = {
    jwtToken: "",
    currentUserEmail: "",
    refreshToken: "",
    expirationTime: 0,
    auth_Time: 0,
    customRoleType: "",
    userId: "",
    userName: "",
    groups: [],
    facilityType: "",
  };
  @observable authMode: AuthMode = "legacy_cognito_role";
  @observable featureFlags: IUserContextFeatureFlags = {
    allowMultiRole: false,
    allowRoleSwitching: false,
    allowFacilitySwitching: false,
  };
  @observable activeRoleType: string = "";
  @observable activeFacilityType: string = "";
  @observable availableRoles: string[] = [];
  @observable availableFacilities: string[] = [];
  @observable memberships: any[] = [];
  @observable currentUserContext: IUserContextResponse | null = null;
  @observable userPoolProps: CognitoUserPool;

  constructor() {
    this.userPoolProps = new CognitoUserPool({
      UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID!,
      ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID!,
    });

    this.hydrateSessionFromStorage();
  }

  @action
  setUserProperties(cognitoUserSession: CognitoUserSession, userName: string) {
    this.sessionProp.expirationTime = cognitoUserSession.getIdToken().getExpiration();
    this.sessionProp.auth_Time = cognitoUserSession.getIdToken().getIssuedAt();
    this.sessionProp.jwtToken = cognitoUserSession.getIdToken().getJwtToken();
    this.sessionProp.currentUserEmail = cognitoUserSession.getIdToken().decodePayload()["email"];
    this.sessionProp.refreshToken = cognitoUserSession.getRefreshToken().getToken();
    this.sessionProp.customRoleType = cognitoUserSession.getIdToken().decodePayload()["custom:custom_role_type"];
    this.sessionProp.userId = cognitoUserSession.getIdToken().decodePayload()["sub"];
    this.sessionProp.userName = cognitoUserSession.getIdToken().decodePayload()["cognito:username"];
    this.sessionProp.groups = this.normalizeGroups(cognitoUserSession.getIdToken().decodePayload()["cognito:groups"]);
    this.sessionProp.facilityType = cognitoUserSession.getIdToken().decodePayload()["custom:facility_type"] || "";
    this.applyLegacyContextFallback();
  }

  @action async isUserLoggedIn(): Promise<boolean> {
    this.cognitoUser = this.userPoolProps.getCurrentUser()!;
    if (this.cognitoUser === null || this.cognitoUser === undefined) {
      return Promise.resolve(false);
    }

    const sessionData = await this.getSessionFromCognito(this.cognitoUser);
    this.setUserProperties(sessionData, this.cognitoUser.getUsername());
    await this.loadUserContext().catch(() => null);
    return Promise.resolve(true);
  }

  @action async waitForJwtToken() {
    while (this.sessionProp.jwtToken === "") {
      await this.sleep(500);
    }
    return this.sessionProp.jwtToken;
  }

  @action getUserRoleType() {
    this.hydrateSessionFromStorage();
    if (this.authMode === "multi_role_context" && this.activeRoleType) {
      return this.activeRoleType;
    }
    if (this.sessionProp != null) {
      return this.sessionProp.customRoleType;
    }
  }

  @action getUserGroups() {
    this.hydrateSessionFromStorage();
    if (this.sessionProp != null) {
      return this.sessionProp.groups;
    }
  }

  @action isAdmin() {
    return this.getUserGroups()?.includes(USER_GROUP_ADMIN) || this.currentUserContext?.user?.platformAdmin === true || this.getUserRoleType() === ROLE_TYPE_ADMIN;
  }

  @action getUserId() {
    this.hydrateSessionFromStorage();
    if (this.sessionProp != null) {
      return this.sessionProp.userId;
    }
  }

  @action getUserFacilityType() {
    this.hydrateSessionFromStorage();
    if (this.authMode === "multi_role_context" && this.activeFacilityType) {
      return this.activeFacilityType;
    }
    if (this.sessionProp != null) {
      return this.sessionProp.facilityType;
    }
  }

  @action getUserName() {
    this.hydrateSessionFromStorage();
    if (this.sessionProp != null) {
      return this.sessionProp.userName;
    }
  }

  @action getUserEmailId() {
    this.hydrateSessionFromStorage();
    if (this.sessionProp != null) {
      return this.sessionProp.currentUserEmail;
    }
  }

  @action getAuthMode() {
    return this.authMode;
  }

  @action getFeatureFlags() {
    return this.featureFlags;
  }

  @action async loadUserContext(forceRefresh: boolean = false): Promise<IUserContextResponse | null> {
    this.hydrateSessionFromStorage();
    if (!this.sessionProp.jwtToken) {
      return null;
    }

    if (!forceRefresh && this.currentUserContext && this.currentUserContext.user) {
      return this.currentUserContext;
    }

    if (!forceRefresh && this.userContextPromise) {
      return this.userContextPromise;
    }

    const params = new URLSearchParams();
    if (typeof window !== "undefined" && window.location?.host) {
      params.set("host", window.location.host);
    }
    const brandOverride = getSessionBrandOverride();
    if (brandOverride) {
      params.set("brandOverride", brandOverride);
    }

    this.userContextPromise = axios.get(
      `${this.AUTH_API_ENDPOINT}/me/context${params.toString() ? `?${params.toString()}` : ""}`,
      {
        headers: {
          authorization: this.sessionProp.jwtToken,
        },
      },
    ).then((response) => {
      const context = (response?.data || null) as IUserContextResponse | null;
      this.applyUserContext(context);
      return context;
    }).catch((error) => {
      this.applyLegacyContextFallback();
      throw error;
    }).finally(() => {
      this.userContextPromise = null;
    });

    return this.userContextPromise;
  }

  @action signOut() {
    if (this.cognitoUser != null) {
      this.cognitoUser.signOut();
      this.cognitoUser = undefined;
      this.resetUserContext();
      window.location.replace(RouterAddress.LOGIN_PAGE);
    }
  }

  @action setPendingNewPasswordUser(user: CognitoUser | null) {
    this.pendingNewPasswordUser = user;
  }

  getPendingNewPasswordUser() {
    return this.pendingNewPasswordUser;
  }

  @action clearPendingNewPasswordUser() {
    this.pendingNewPasswordUser = null;
  }

  @action async refreshJwtToken(): Promise<string> {
    if (this.refreshSessionPromise) {
      return this.refreshSessionPromise;
    }

    this.refreshSessionPromise = new Promise<string>((resolve, reject) => {
      try {
        this.cognitoUser = this.userPoolProps.getCurrentUser()!;
        if (!this.cognitoUser) {
          reject(new Error("No authenticated user session was found."));
          return;
        }

        const refreshTokenValue = this.sessionProp.refreshToken || this.getStoredRefreshToken();
        if (!refreshTokenValue) {
          reject(new Error("No refresh token is available."));
          return;
        }

        const refreshToken = new CognitoRefreshToken({ RefreshToken: refreshTokenValue });
        this.cognitoUser.refreshSession(refreshToken, async (err, session) => {
          if (err) {
            reject(err);
            return;
          }
          if (!session || !session.isValid()) {
            reject(new Error("Refreshed session is not valid."));
            return;
          }

          this.setUserProperties(session, this.cognitoUser!.getUsername());
          await this.loadUserContext(true).catch(() => null);
          resolve(this.sessionProp.jwtToken);
        });
      } catch (error) {
        reject(error);
      }
    }).finally(() => {
      this.refreshSessionPromise = null;
    });

    return this.refreshSessionPromise;
  }

  private sleep(time: number) {
    return new Promise((resolve) => {
      setTimeout(() => {
        // @ts-ignore
        resolve(undefined);
      }, time);
    });
  }

  @action private hydrateSessionFromStorage() {
    if (this.sessionProp.userId) {
      return;
    }

    try {
      const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID!;
      const lastAuthUserKey = `CognitoIdentityServiceProvider.${clientId}.LastAuthUser`;
      const lastAuthUser = localStorage.getItem(lastAuthUserKey);
      if (!lastAuthUser) {
        return;
      }

      const tokenPrefix = `CognitoIdentityServiceProvider.${clientId}.${lastAuthUser}`;
      const idToken = localStorage.getItem(`${tokenPrefix}.idToken`);
      const refreshToken = localStorage.getItem(`${tokenPrefix}.refreshToken`) || "";

      if (!idToken) {
        return;
      }

      const payload = this.decodeJwtPayload(idToken);
      this.sessionProp.jwtToken = idToken;
      this.sessionProp.refreshToken = refreshToken;
      this.sessionProp.expirationTime = payload?.exp || 0;
      this.sessionProp.auth_Time = payload?.auth_time || payload?.iat || 0;
      this.sessionProp.currentUserEmail = payload?.email || "";
      this.sessionProp.customRoleType = payload?.["custom:custom_role_type"] || "";
      this.sessionProp.userId = payload?.sub || "";
      this.sessionProp.userName = payload?.["cognito:username"] || lastAuthUser;
      this.sessionProp.groups = this.normalizeGroups(payload?.["cognito:groups"]);
      this.sessionProp.facilityType = payload?.["custom:facility_type"] || "";
      this.applyLegacyContextFallback();
    } catch (error) {
      // Ignore hydration failures and fallback to isUserLoggedIn flow.
    }
  }

  @action private applyUserContext(context: IUserContextResponse | null) {
    const resolvedContext = context || {};
    this.currentUserContext = context;
    this.authMode = resolvedContext.authMode === "multi_role_context" ? "multi_role_context" : "legacy_cognito_role";
    this.featureFlags = {
      allowMultiRole: resolvedContext.featureFlags?.allowMultiRole === true,
      allowRoleSwitching: resolvedContext.featureFlags?.allowRoleSwitching === true,
      allowFacilitySwitching: resolvedContext.featureFlags?.allowFacilitySwitching === true,
    };
    this.memberships = Array.isArray(resolvedContext.memberships) ? resolvedContext.memberships : [];
    this.availableRoles = Array.isArray(resolvedContext.availableRoles)
      ? resolvedContext.availableRoles.map((role) => String(role || "").trim()).filter(Boolean)
      : [];
    this.availableFacilities = Array.isArray(resolvedContext.availableFacilities)
      ? resolvedContext.availableFacilities.map((facility) => String(facility || "").trim()).filter(Boolean)
      : [];
    this.activeRoleType = String(
      resolvedContext.activeContext?.roleType
      || resolvedContext.user?.legacyRoleType
      || this.sessionProp.customRoleType
      || "",
    ).trim().toLowerCase();
    this.activeFacilityType = String(
      resolvedContext.activeContext?.facilityType
      || resolvedContext.user?.legacyFacilityType
      || this.sessionProp.facilityType
      || "",
    ).trim().toLowerCase();
  }

  @action private applyLegacyContextFallback() {
    this.authMode = "legacy_cognito_role";
    this.featureFlags = {
      allowMultiRole: false,
      allowRoleSwitching: false,
      allowFacilitySwitching: false,
    };
    this.activeRoleType = String(this.sessionProp.customRoleType || "").trim().toLowerCase();
    this.activeFacilityType = String(this.sessionProp.facilityType || "").trim().toLowerCase();
    this.availableRoles = this.activeRoleType ? [this.activeRoleType] : [];
    this.availableFacilities = this.activeFacilityType ? [this.activeFacilityType] : [];
    this.memberships = [];
  }

  @action private resetUserContext() {
    this.currentUserContext = null;
    this.userContextPromise = null;
    this.authMode = "legacy_cognito_role";
    this.featureFlags = {
      allowMultiRole: false,
      allowRoleSwitching: false,
      allowFacilitySwitching: false,
    };
    this.activeRoleType = "";
    this.activeFacilityType = "";
    this.availableRoles = [];
    this.availableFacilities = [];
    this.memberships = [];
  }

  private decodeJwtPayload(token: string): any {
    const tokenParts = token.split(".");
    if (tokenParts.length < 2) {
      return {};
    }

    const base64 = tokenParts[1].replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((char) => "%" + ("00" + char.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );

    return JSON.parse(jsonPayload);
  }

  private getStoredRefreshToken(): string {
    try {
      const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID!;
      const lastAuthUserKey = `CognitoIdentityServiceProvider.${clientId}.LastAuthUser`;
      const lastAuthUser = localStorage.getItem(lastAuthUserKey);
      if (!lastAuthUser) {
        return "";
      }

      const tokenPrefix = `CognitoIdentityServiceProvider.${clientId}.${lastAuthUser}`;
      return localStorage.getItem(`${tokenPrefix}.refreshToken`) || "";
    } catch {
      return "";
    }
  }

  getSessionFromCognito(cognitoUser: CognitoUser): Promise<CognitoUserSession> {
    return new Promise((resolve) => {
      cognitoUser.getSession(function (err: any, session: any) {
        if (err) {
          throw err;
        }
        if (!session.isValid()) {
          throw new Error("Session Is Not Valid");
        }
        resolve(session);
      });
    });
  }

  @action getNewSession() {
    this.cognitoUser = this.userPoolProps.getCurrentUser()!;
    return new Promise((resolve) => {
      this.cognitoUser!.getSession(function (err: any, session: any) {
        if (err) {
          throw err;
        }
        if (!session.isValid()) {
          throw new Error("Session Is Not Valid");
        }
        resolve(session);
      });
    });
  }

  @action getUserAttributesFromCognito(): Promise<CognitoUserAttribute[]> {
    return new Promise((resolve, reject) => {
      this.cognitoUser!.getUserAttributes((err, userAttributes) => {
        if (err) {
          reject(err);
        }
        resolve(userAttributes!);
      });
    });
  }

  @action updateUserAttributesInCognito(
    userAttributes: ICognitoUserAttributeData[],
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      this.cognitoUser!.updateAttributes(userAttributes, (err) => {
        if (err) {
          reject(err);
        }
        // @ts-ignore
        resolve(undefined);
      });
    });
  }
}
